<template>
<!-- eslint-disable max-len -->
<div class="container">
<div class="row no-gutters">
<div class="list-form col-sm-12">
  <router-link :to="`${pagePrefix}/create`" class="btn create-btn float-sm-right">Create {{ name | capitalize }}</router-link>

  <div class="form-inline float-md-right float-sm-none my-2 my-md-0">
    <div>
    <select-input
      v-if="sortOptions && sortOptions.length > 0"
      label
      v-model="selectedSort"
      :options="sortOptionsWithDefault"
      aria-label="Sort By"
      style="transform: scale(0.8);"
      @input="onSort"
    >
      Sort By:&nbsp;
    </select-input>
    </div>
    <input
      placeholder="Search"
      v-model="term"
      class="form-control mr-2"
      type="search"
      aria-label="Search"
      v-focus
    />
    <button
      v-if="remove"
      class="btn"
      style="margin-right: 25px;"
      @click.prevent="() => { $refs.deleteConfirmModal.show(); }"
      :disabled="deleting"
      aria-label="Delete All Filtered Items In This List"
    >
      Multi-Delete
    </button>
    <b-modal
      ref="deleteConfirmModal"
      title="Confirm Multi-Delete"
      size="sm"
      ok-variant="danger"
      ok-title="Delete!"
      @ok="deleteAllFilteredItems"
    >
      WARNING: you are about to delete all {{filtered.length}} items<span v-if="term"> that match the filter "{{term}}"</span>.
      <strong>THIS IS IRREVERSIBLE.</strong>
    </b-modal>
  </div>

  <h2>{{ pluralString | capitalize }}</h2>

  <ul>
    <router-link :to="`${pagePrefix}/${model[use]}/edit`" tag="li" :key="model[use]" v-for="model in sorted">
      <img v-if="icon" class="icon" :src="resolveAssetPath(model.icon, 'characters')">
      <paragraphs v-if="!displayHtml" no-margin>{{ display(model) }}</paragraphs>
      <div v-else v-html="display(model)"></div>
    </router-link>
  </ul>
</div>
</div>
</div>
</template>

<script>
import { mapActions } from 'vuex';

import Paragraphs from '../../ui/display/Paragraphs';
import SelectInput from '../../ui/inputs/SelectInput';

export default {
  props: {
    // name of the type of thing being listed
    name: { type: String, required: true },
    // if unspecified, adds an s to name; specify this when that would be incorrect
    plural: { type: String },
    // enables icon, currently only used by characters list
    icon: { type: Boolean },
    // key field, for navigating to edit page (in general, use _id)
    use: { type: String, required: true },
    // (model) => display string
    display: { type: Function, required: true },
    // whether to pass the display string to v-html (WARNING: XSS vector)
    displayHtml: { type: Boolean },
    // override Alphabetical sort function (in general, leave unspecified)
    sort: { type: Function },
    // override Alphabetical sort name (in general, leave unspecified)
    sortName: { type: String },
    // prefix of routes, assumes conventions /create, /:id/edit
    pagePrefix: { type: String, required: true },
    // gets models, returns Promise
    fetch: { type: Function, required: true },
    // used for multi-delete (currently utilizing single-delete multiple times)
    remove: { type: Function },
    // array of strings (used to select the function)
    sortOptions: { type: Array },
    // array of functions (used to do the sort)
    sortFunctions: { type: Array },
    // index into sortOptions, if unspecified use built-in default Alphabetical sort
    defaultSort: { type: Number, default: -1 },
  },
  components: {
    Paragraphs,
    SelectInput
  },
  data() {
    return {
      models: [],
      term: '',
      selectedSort: 'Alphabetical',
      deleting: false,
    };
  },
  async created() {
    try {
      const models = await this.fetch();
      this.models = models;
    } catch (_) {
      const message = `Error fetching ${this.pluralString}, please try again`;
      this.flashDanger({ message });
    }
  },
  mounted() {
    if (this.$route.query && this.$route.query.sort) {
      this.selectedSort = this.$route.query.sort;
    } else if (this.defaultSort >= 0 && this.defaultSort < this.sortOptions.length) {
      this.selectedSort = this.sortOptions[this.defaultSort];
    } else {
      this.selectedSort = this.sortName || 'Alphabetical';
    }
  },
  computed: {
    pluralString() {
      return this.plural ? this.plural : `${this.name}s`;
    },
    modelsWithDisplay() {
      this.models.forEach((model) => {
        // eslint-disable-next-line no-underscore-dangle
        model._display = this.display(model);
      });
      return this.models;
    },
    filtered() {
      if (!this.term) return this.modelsWithDisplay;

      const lowerCaseSearch = this.term.toLowerCase();

      return this.modelsWithDisplay.filter(model => (
        this.display(model).toLowerCase().includes(lowerCaseSearch)
      ));
    },
    sorted() {
      return this.filtered.slice().sort(this.selectedSortFunction);
    },
    sortOptionsWithDefault() {
      return [(this.sortName || 'Alphabetical'), ...(this.sortOptions)];
    },
    selectedSortFunction() {
      if (this.selectedSort === (this.sortName || 'Alphabetical')) {
        return this.sort || this.sortByDisplay;
      }
      const index = this.sortOptions.indexOf(this.selectedSort);
      if (index < 0 || index > this.sortFunctions.length - 1) {
        return this.sort || this.sortByDisplay;
      }
      return this.sortFunctions[index];
    }
  },
  methods: {
    ...mapActions([
      'flashDanger',
      'flashSuccess',
    ]),
    sortByDisplay(a, b) {
      // eslint-disable-next-line no-underscore-dangle
      return a._display < b._display ? -1 : a._display > b._display ? 1 : 0;
    },
    onSort() {
      // TODO: figure out some way to prevent page reloading when changing the query params
      this.$router.replace({ query: { sort: this.selectedSort } });
    },
    async deleteAllFilteredItems() {
      if (this.deleting || !(this.remove)) return;
      this.deleting = true;

      let error = false;

      const removeAsync = async(item) => {
        try {
          await this.remove(item);
          const message = `${item.name} deleted successfully`;
          console.log(message);
        } catch (_) {
          error = true;
          const message = `Error deleting ${item.name}`;
          console.error(message);
        }
      };

      await Promise.all(this.filtered.map(item => removeAsync(item)));

      if (error) {
        const message = 'Error while deleting; check console for details';
        this.flashDanger({ message });
      } else {
        const message = `${this.filtered.length} items deleted successfully`;
        this.flashSuccess({ message });
      }
      this.deleting = false;
      await new Promise(resolve => setTimeout(resolve, 1000));
      this.$router.go(0);
    }
  }
};
</script>

<style lang="scss" scoped>
.list-form {
  margin-top: 50px;
  padding: 20px;
  background-color: white;
}
.list-form ul {
  list-style: none;
  padding-left: 0;
}
.list-form li {
  color: black;
  border: 2px solid black;
  padding: 5px;
  margin-bottom: 5px;
}
.list-form li:hover {
  border: 2px solid;
  border-color: var(--primaryColor);
  background-color: var(--primaryColor);
  color: white;
}
.list-form li p {
  margin: 0;
}
.list-form li p:first-of-type {
  font-weight: bold;
}
.icon {
  max-height: 75px;
}
.create-btn {
  margin: 0;
  line-height: 2em;
  padding: 2px 10px 3px 10px;
}
</style>



// WEBPACK FOOTER //
// src/components/ui/pages/ListPage.vue